import React, { useRef, useEffect, useState, useCallback } from 'react';
import './DesignCanvas.css';
import { detectCollisions, CollisionResult } from '../../utils/collisionDetection';
import { calculateGridConvergence } from '../../utils/geotiff';
import { ColorTheme } from '../../types/themes';
import themeCache from '../../services/themeCache';

interface LocationMarker {
  id: string;
  locationId: string;
  name: string;
  lat: number;
  long: number;
  dataProduct: 'METAR' | 'Marine' | 'Trails';
  pixelX: number;
  pixelY: number;
  manuallyAdjusted: boolean;
}

interface Overlay {
  id: string;
  type: 'legend' | 'logo' | 'title';
  visible: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  config: any;
}

interface ViewState {
  centerLat: number;
  centerLong: number;
  zoomLevel: number;
  pixelOffsetX: number;
  pixelOffsetY: number;
}

interface PhysicalSize {
  width: number;
  height: number;
  unit: 'inches';
}

interface LoadedGeoTIFF {
  metadata: {
    bounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    };
    width: number;
    height: number;
  };
  geoToPixel: (coord: { latitude: number; longitude: number }) => { x: number; y: number };
  pixelToGeo: (coord: { x: number; y: number }) => { latitude: number; longitude: number };
}

interface DesignCanvasProps {
  baseMapImage: HTMLImageElement | null;
  loadedGeoTIFF: LoadedGeoTIFF | null;
  locations: LocationMarker[];
  overlays?: Overlay[];
  viewState: ViewState;
  physicalSize: PhysicalSize;
  activeTheme?: ColorTheme | null;
  selectedAttribute?: string;
  locationDataMap?: Map<string, any>;
  
  // Multi-tile support
  baseMapType?: 'single-tile' | 'multi-tile';
  loadedTiles?: any[]; // LoadedTile[] from TileManager
  tileOffsets?: Map<string, { x: number; y: number }>;
  stitchedBounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null;
  stitchedCanvas?: HTMLCanvasElement | null;
  
  onViewChange: (viewState: ViewState) => void;
  onLocationDrag?: (locationId: string, newPixelX: number, newPixelY: number) => void;
  onLocationDragEnd?: (locationId: string, newPixelX: number, newPixelY: number) => void;
  onOverlayDrag?: (overlayId: string, newPosition: { x: number; y: number }) => void;
  onOverlayDragEnd?: (overlayId: string, newPosition: { x: number; y: number }) => void;
  onUndoManualAdjustment?: (locationId: string) => void;
  onCollisionChange?: (collisionCount: number, collidingMarkerIds: Set<string>, collisions: Array<{location1Id: string, location2Id: string, distance: number}>) => void;
  onMapExtentChange?: (mapExtent: { north: number; south: number; east: number; west: number }) => void;
}

export const DesignCanvas: React.FC<DesignCanvasProps> = ({
  baseMapImage,
  loadedGeoTIFF,
  locations,
  overlays = [],
  viewState,
  physicalSize,
  activeTheme,
  selectedAttribute,
  locationDataMap,
  baseMapType = 'single-tile',
  loadedTiles,
  tileOffsets,
  stitchedBounds,
  stitchedCanvas,
  onViewChange,
  onLocationDrag,
  onLocationDragEnd,
  onOverlayDrag,
  onOverlayDragEnd,
  onUndoManualAdjustment,
  onCollisionChange,
  onMapExtentChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Helper function to get marker color from theme
  const getMarkerColorFromTheme = useCallback((location: LocationMarker): string => {
    // If no theme or attribute, use default color
    if (!activeTheme || !selectedAttribute) {
      return '#4CAF50'; // Default green
    }
    
    // Get location data from the map
    const locationData = locationDataMap?.get(location.locationId);
    if (!locationData) {
      return '#4CAF50'; // Default green if no data
    }
    
    // Get the value for the selected attribute from location data
    const value = locationData[selectedAttribute];
    if (value === undefined || value === null) {
      return '#4CAF50'; // Default green if attribute not found
    }
    
    // Use theme cache to determine color
    return themeCache.getColorForValue(activeTheme, value);
  }, [activeTheme, selectedAttribute, locationDataMap]);
  
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  
  // Drag state for location markers
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [draggedMarkerId, setDraggedMarkerId] = useState<string | null>(null);
  const [draggedMarkerCoords, setDraggedMarkerCoords] = useState<{ lat: number; long: number } | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  
  // Drag state for overlays
  const [isDraggingOverlay, setIsDraggingOverlay] = useState(false);
  const [draggedOverlayId, setDraggedOverlayId] = useState<string | null>(null);
  const [hoveredOverlayId, setHoveredOverlayId] = useState<string | null>(null);
  const [overlayDragOffset, setOverlayDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  
  // Collision detection state
  const [collisionResult, setCollisionResult] = useState<CollisionResult>({
    collisions: [],
    collidingMarkerIds: new Set<string>(),
    collisionCount: 0,
  });

  // Calculate canvas dimensions based on physical size at a fixed scale
  // CRITICAL: Canvas must represent actual print dimensions at a consistent scale
  // so that what you see in the UI matches what will be in the output files
  const calculateCanvasDimensions = useCallback(() => {
    // Use a fixed scale: 50 pixels per inch
    // This means a 20x30 inch print will be 1000x1500 pixels on screen
    const PIXELS_PER_INCH = 50;
    
    const width = physicalSize.width * PIXELS_PER_INCH;
    const height = physicalSize.height * PIXELS_PER_INCH;
    
    return { width, height };
  }, [physicalSize]);

  // Detect collisions whenever locations, physicalSize, viewState (zoom), or base map change.
  // Marker pixelX/pixelY are in GeoTIFF pixel space. The number of GeoTIFF pixels
  // visible in the physical print area depends on zoom, so zoom affects physical distance.
  useEffect(() => {
    const { width, height } = calculateCanvasDimensions();
    
    if (locations.length > 0 && width > 0 && height > 0) {
      // Calculate how many GeoTIFF pixels are visible in the print area at current zoom.
      // The canvas is (physicalSize * PIXELS_PER_INCH) pixels, and the base map is drawn
      // at scale = zoomLevel / 100. So the visible GeoTIFF extent in pixels is:
      //   visibleGeoPixels = canvasPixels / scale
      const scale = viewState.zoomLevel / 100;
      const visibleGeoWidth = width / scale;
      const visibleGeoHeight = height / scale;
      
      // Pass the visible GeoTIFF extent as the reference dimensions so that
      // marker pixel distances are correctly converted to physical distances.
      const result = detectCollisions(locations, physicalSize, visibleGeoWidth, visibleGeoHeight);
      setCollisionResult(result);
      
      // Notify parent component of collision changes
      if (onCollisionChange) {
        onCollisionChange(result.collisionCount, result.collidingMarkerIds, result.collisions);
      }
    } else {
      // No locations or invalid dimensions - clear collisions
      const emptyResult = {
        collisions: [],
        collidingMarkerIds: new Set<string>(),
        collisionCount: 0,
      };
      setCollisionResult(emptyResult);
      
      if (onCollisionChange) {
        onCollisionChange(0, new Set<string>(), []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, physicalSize, viewState.zoomLevel, calculateCanvasDimensions]);

  // Convert canvas coordinates to marker pixel coordinates
  const canvasToMarkerPixel = useCallback((canvasX: number, canvasY: number): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const { width, height } = calculateCanvasDimensions();
    const scale = viewState.zoomLevel / 100;
    
    // Reverse the transformation used in rendering
    const markerX = (canvasX - (width / 2) - viewState.pixelOffsetX) / scale;
    const markerY = (canvasY - (height / 2) - viewState.pixelOffsetY) / scale;
    
    return { x: markerX, y: markerY };
  }, [viewState, calculateCanvasDimensions]);

  // Find marker at canvas coordinates
  const findMarkerAtPosition = useCallback((canvasX: number, canvasY: number): LocationMarker | null => {
    const canvas = canvasRef.current;
    if (!canvas || !baseMapImage) return null;
    
    const { width, height } = calculateCanvasDimensions();
    const scale = viewState.zoomLevel / 100;
    const hitRadius = 10; // Pixels
    
    // Calculate base map position
    const scaledWidth = baseMapImage.width * scale;
    const scaledHeight = baseMapImage.height * scale;
    const baseMapOffsetX = (width / 2) - (scaledWidth / 2) + viewState.pixelOffsetX;
    const baseMapOffsetY = (height / 2) - (scaledHeight / 2) + viewState.pixelOffsetY;
    
    for (const location of locations) {
      // Transform marker position to canvas coordinates
      const x = baseMapOffsetX + (location.pixelX * scale);
      const y = baseMapOffsetY + (location.pixelY * scale);
      
      const distance = Math.sqrt(Math.pow(canvasX - x, 2) + Math.pow(canvasY - y, 2));
      
      if (distance <= hitRadius) {
        return location;
      }
    }
    
    return null;
  }, [locations, viewState, calculateCanvasDimensions, baseMapImage]);

  // Find overlay at canvas coordinates
  const findOverlayAtPosition = useCallback((canvasX: number, canvasY: number): Overlay | null => {
    if (!overlays || overlays.length === 0) return null;
    
    // Check overlays in reverse order (top to bottom)
    for (let i = overlays.length - 1; i >= 0; i--) {
      const overlay = overlays[i];
      if (!overlay.visible) continue;
      
      const { x, y } = overlay.position;
      const { width, height } = overlay.size;
      
      if (
        canvasX >= x &&
        canvasX <= x + width &&
        canvasY >= y &&
        canvasY <= y + height
      ) {
        return overlay;
      }
    }
    
    return null;
  }, [overlays]);

  // Render the canvas
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Helper function to render legend content using theme
    const renderLegendContent = (ctx: CanvasRenderingContext2D, overlayWidth: number, overlayHeight: number) => {
      ctx.font = 'bold 14px Arial';
      ctx.fillStyle = '#333';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('Legend', 10, 10);
      
      // Draw theme categories/ranges with colored dots
      let yOffset = 35;
      const lineHeight = 20;
      const dotRadius = 5;
      const dotX = 15;
      
      if (activeTheme && activeTheme.mappings) {
        // Display theme mappings (categories or ranges)
        activeTheme.mappings.slice(0, Math.floor((overlayHeight - 40) / lineHeight)).forEach((mapping) => {
          // Draw colored dot
          ctx.beginPath();
          ctx.arc(dotX, yOffset + 6, dotRadius, 0, 2 * Math.PI);
          ctx.fillStyle = mapping.color;
          ctx.fill();
          ctx.strokeStyle = '#333';
          ctx.lineWidth = 1;
          ctx.stroke();
          
          // Draw mapping label
          ctx.font = '12px Arial';
          ctx.fillStyle = '#333';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          ctx.fillText(mapping.label, dotX + dotRadius + 8, yOffset);
          
          yOffset += lineHeight;
        });
      } else {
        // Fallback: show "No theme selected"
        ctx.font = '12px Arial';
        ctx.fillStyle = '#666';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText('No theme selected', 10, yOffset);
      }
    };
    
    const { width, height } = calculateCanvasDimensions();
    canvas.width = width;
    canvas.height = height;
    
    // Clear canvas
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, width, height);
    
    // Calculate the actual center longitude of the visible area for grid convergence
    // This must match the actual map centering logic to ensure rotation updates correctly
    let actualCenterLong = viewState.centerLong;
    
    if (baseMapImage && loadedGeoTIFF) {
      // Use the same method as map centering: convert viewState center to pixel, then account for pan offset
      const scale = viewState.zoomLevel / 100;
      
      // Get the pixel coordinates of the viewState center point
      const centerPixel = loadedGeoTIFF.geoToPixel({
        latitude: viewState.centerLat,
        longitude: viewState.centerLong
      });
      
      // Calculate where this pixel appears on the canvas after scaling and panning
      const scaledWidth = baseMapImage.width * scale;
      const scaledHeight = baseMapImage.height * scale;
      const baseMapOffsetX = (width / 2) - (centerPixel.x * scale) + viewState.pixelOffsetX;
      const baseMapOffsetY = (height / 2) - (centerPixel.y * scale) + viewState.pixelOffsetY;
      
      // Now find which pixel is actually at the canvas center after panning
      const canvasCenterX = width / 2;
      const canvasCenterY = height / 2;
      
      // Convert canvas center back to base map pixel coordinates
      const actualCenterPixelX = (canvasCenterX - baseMapOffsetX) / scale;
      const actualCenterPixelY = (canvasCenterY - baseMapOffsetY) / scale;
      
      // Convert pixel coordinates back to geographic coordinates using pixelToGeo
      const actualCenter = loadedGeoTIFF.pixelToGeo({
        x: actualCenterPixelX,
        y: actualCenterPixelY
      });
      
      actualCenterLong = actualCenter.longitude;
    }
    
    // Calculate grid convergence angle for rotation correction
    const gridConvergence = calculateGridConvergence(actualCenterLong);
    const rotationAngleRadians = (gridConvergence * Math.PI) / 180;
    
    // Save canvas state before rotation
    ctx.save();
    
    // Translate to canvas center, rotate, then translate back
    // This rotates the entire canvas around its center point
    // Positive rotation = counter-clockwise, which corrects for grid convergence
    ctx.translate(width / 2, height / 2);
    ctx.rotate(rotationAngleRadians); // Positive rotation to correct grid convergence
    ctx.translate(-width / 2, -height / 2);
    
    // Calculate base map position and scale
    const scale = viewState.zoomLevel / 100;
    let baseMapOffsetX = 0;
    let baseMapOffsetY = 0;
    let scaledWidth = 0;
    let scaledHeight = 0;
    
    // Draw base map if available
    if (baseMapType === 'single-tile' && baseMapImage) {
      scaledWidth = baseMapImage.width * scale;
      scaledHeight = baseMapImage.height * scale;
      
      // Convert viewState center coordinates to pixel coordinates
      // CRITICAL: Use loadedGeoTIFF.geoToPixel() for proper Lambert Conformal Conic projection
      // Simple linear interpolation causes centering errors due to projection distortion
      let centerPixelX: number;
      let centerPixelY: number;
      
      if (loadedGeoTIFF) {
        // Use proper projection transformation - REQUIRED, no fallback
        const centerPixel = loadedGeoTIFF.geoToPixel({
          latitude: viewState.centerLat,
          longitude: viewState.centerLong
        });
        centerPixelX = centerPixel.x;
        centerPixelY = centerPixel.y;
      } else {
        // CRITICAL: If GeoTIFF not loaded, we cannot render accurately
        // This should never happen in production - the map should not be displayed without GeoTIFF
        console.error('CRITICAL: Cannot render map without loaded GeoTIFF - projection transformation required');
        return;
      }
      
      // Calculate offsets to position these pixels at the canvas center
      // Canvas center is at (width/2, height/2)
      // We want centerPixelX/Y (scaled) to appear at canvas center
      baseMapOffsetX = (width / 2) - (centerPixelX * scale) + viewState.pixelOffsetX;
      baseMapOffsetY = (height / 2) - (centerPixelY * scale) + viewState.pixelOffsetY;
      
      // DEBUG: Log map extent for comparison with Lambda
      if (loadedGeoTIFF) {
        // Calculate visible region in overview pixel coordinates
        const visibleWidthOverview = width / scale;
        const visibleHeightOverview = height / scale;
        
        // Calculate the actual center after pan offsets
        const actualCenterPixelX = (width / 2 - baseMapOffsetX) / scale;
        const actualCenterPixelY = (height / 2 - baseMapOffsetY) / scale;
        
        // Calculate visible region bounds in overview coordinates
        const leftOverview = actualCenterPixelX - (visibleWidthOverview / 2);
        const topOverview = actualCenterPixelY - (visibleHeightOverview / 2);
        const rightOverview = actualCenterPixelX + (visibleWidthOverview / 2);
        const bottomOverview = actualCenterPixelY + (visibleHeightOverview / 2);
        
        // Convert corners to geographic coordinates
        const nwCorner = loadedGeoTIFF.pixelToGeo({ x: leftOverview, y: topOverview });
        const seCorner = loadedGeoTIFF.pixelToGeo({ x: rightOverview, y: bottomOverview });
        
        const mapExtent = {
          north: nwCorner.latitude,
          south: seCorner.latitude,
          west: nwCorner.longitude,
          east: seCorner.longitude
        };
        
        // Pass map extent to parent component
        if (onMapExtentChange) {
          onMapExtentChange(mapExtent);
        }
        
        console.log('=== FRONTEND MAP EXTENT DEBUG ===');
        console.log(`ViewState center: lat=${viewState.centerLat.toFixed(6)}, long=${viewState.centerLong.toFixed(6)}`);
        console.log(`Center pixel in overview: (${centerPixelX.toFixed(1)}, ${centerPixelY.toFixed(1)})`);
        console.log(`Actual center after offsets: (${actualCenterPixelX.toFixed(1)}, ${actualCenterPixelY.toFixed(1)})`);
        console.log(`Visible region in overview coords: left=${leftOverview.toFixed(1)}, top=${topOverview.toFixed(1)}, right=${rightOverview.toFixed(1)}, bottom=${bottomOverview.toFixed(1)}`);
        console.log(`Map extent: north=${mapExtent.north.toFixed(6)}, south=${mapExtent.south.toFixed(6)}, west=${mapExtent.west.toFixed(6)}, east=${mapExtent.east.toFixed(6)}`);
        console.log(`Zoom: ${viewState.zoomLevel}%, Offsets: (${viewState.pixelOffsetX}, ${viewState.pixelOffsetY})`);
        console.log('=================================');
      }
      
      ctx.drawImage(
        baseMapImage,
        baseMapOffsetX,
        baseMapOffsetY,
        scaledWidth,
        scaledHeight
      );
    } else if (baseMapType === 'multi-tile' && stitchedCanvas) {
      // Multi-tile rendering
      scaledWidth = stitchedCanvas.width * scale;
      scaledHeight = stitchedCanvas.height * scale;
      
      // For multi-tile, we need to use the stitched bounds
      if (!stitchedBounds) {
        console.error('CRITICAL: Cannot render multi-tile map without stitched bounds');
        return;
      }
      
      // Calculate center pixel in stitched canvas coordinates
      // Use simple linear interpolation for stitched canvas (already in unified coordinate space)
      const latRange = stitchedBounds.north - stitchedBounds.south;
      const longRange = stitchedBounds.east - stitchedBounds.west;
      
      const normalizedX = (viewState.centerLong - stitchedBounds.west) / longRange;
      const normalizedY = (stitchedBounds.north - viewState.centerLat) / latRange;
      
      const centerPixelX = normalizedX * stitchedCanvas.width;
      const centerPixelY = normalizedY * stitchedCanvas.height;
      
      // Calculate offsets
      baseMapOffsetX = (width / 2) - (centerPixelX * scale) + viewState.pixelOffsetX;
      baseMapOffsetY = (height / 2) - (centerPixelY * scale) + viewState.pixelOffsetY;
      
      // Calculate map extent for multi-tile
      const visibleWidthStitched = width / scale;
      const visibleHeightStitched = height / scale;
      
      const actualCenterPixelX = (width / 2 - baseMapOffsetX) / scale;
      const actualCenterPixelY = (height / 2 - baseMapOffsetY) / scale;
      
      const leftStitched = actualCenterPixelX - (visibleWidthStitched / 2);
      const topStitched = actualCenterPixelY - (visibleHeightStitched / 2);
      const rightStitched = actualCenterPixelX + (visibleWidthStitched / 2);
      const bottomStitched = actualCenterPixelY + (visibleHeightStitched / 2);
      
      // Convert to geographic coordinates
      const pixelsPerDegreeLong = stitchedCanvas.width / longRange;
      const pixelsPerDegreeLat = stitchedCanvas.height / latRange;
      
      const mapExtent = {
        west: stitchedBounds.west + (leftStitched / pixelsPerDegreeLong),
        east: stitchedBounds.west + (rightStitched / pixelsPerDegreeLong),
        north: stitchedBounds.north - (topStitched / pixelsPerDegreeLat),
        south: stitchedBounds.north - (bottomStitched / pixelsPerDegreeLat),
      };
      
      if (onMapExtentChange) {
        onMapExtentChange(mapExtent);
      }
      
      console.log('=== MULTI-TILE MAP EXTENT DEBUG ===');
      console.log(`Stitched canvas: ${stitchedCanvas.width}x${stitchedCanvas.height}`);
      console.log(`Map extent: north=${mapExtent.north.toFixed(6)}, south=${mapExtent.south.toFixed(6)}, west=${mapExtent.west.toFixed(6)}, east=${mapExtent.east.toFixed(6)}`);
      console.log('====================================');
      
      ctx.drawImage(
        stitchedCanvas,
        baseMapOffsetX,
        baseMapOffsetY,
        scaledWidth,
        scaledHeight
      );
    }
    
    // Draw location markers
    // CRITICAL: Markers must be positioned relative to the base map image, not the canvas center
    locations.forEach((location) => {
      // Transform marker pixel coordinates to canvas coordinates
      // The marker's pixelX/pixelY are in the GeoTIFF's coordinate space (0,0 = top-left of image)
      // We need to transform them to canvas space accounting for:
      // 1. The base map's position on the canvas (baseMapOffsetX/Y)
      // 2. The zoom scale
      const x = baseMapOffsetX + (location.pixelX * scale);
      const y = baseMapOffsetY + (location.pixelY * scale);
      
      const isHovered = hoveredMarkerId === location.id;
      const isDragging = draggedMarkerId === location.id;
      const isColliding = collisionResult.collidingMarkerIds.has(location.id);
      
      // Draw marker circle with different styles
      ctx.beginPath();
      ctx.arc(x, y, isDragging ? 8 : isHovered ? 7 : 6, 0, 2 * Math.PI);
      
      // Color based on state - collision takes priority, then theme-based color
      if (isColliding) {
        ctx.fillStyle = '#ff0000'; // Red for collisions (highest priority)
      } else if (isDragging) {
        ctx.fillStyle = '#2196F3'; // Blue when dragging
      } else if (location.manuallyAdjusted) {
        ctx.fillStyle = '#ff6b6b'; // Light red for manually adjusted
      } else {
        // Use theme-based color if available, otherwise default green
        ctx.fillStyle = getMarkerColorFromTheme(location) || '#4CAF50';
      }
      
      ctx.fill();
      ctx.strokeStyle = isHovered || isDragging ? '#000' : '#fff';
      ctx.lineWidth = isHovered || isDragging ? 3 : 2;
      ctx.stroke();
      
      // Draw location name
      ctx.font = isDragging ? 'bold 12px Arial' : '12px Arial';
      ctx.fillStyle = isColliding ? '#ff0000' : '#333';
      ctx.textAlign = 'center';
      ctx.fillText(location.name, x, y - 12);
    });
    
    // Restore canvas state (undo rotation)
    ctx.restore();
    
    // Draw overlays AFTER rotation restore - they stay upright
    // Apply counter-rotation to overlays so they don't rotate with the map
    ctx.save();
    overlays.forEach((overlay) => {
      if (!overlay.visible) return;
      
      const isHovered = hoveredOverlayId === overlay.id;
      const isDragging = draggedOverlayId === overlay.id;
      const { x, y } = overlay.position;
      const { width: overlayWidth, height: overlayHeight } = overlay.size;
      
      // Draw overlay background with border
      ctx.fillStyle = isDragging ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(x, y, overlayWidth, overlayHeight);
      
      ctx.strokeStyle = isDragging ? '#2196F3' : isHovered ? '#666' : '#ccc';
      ctx.lineWidth = isDragging ? 3 : isHovered ? 2 : 1;
      ctx.strokeRect(x, y, overlayWidth, overlayHeight);
      
      // Render overlay content based on type
      ctx.save();
      ctx.translate(x, y);
      
      if (overlay.type === 'title') {
        const config = overlay.config as { text: string; fontSize: number; fontFamily: string };
        ctx.font = `${config.fontSize}px ${config.fontFamily}`;
        ctx.fillStyle = '#333';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(config.text, overlayWidth / 2, overlayHeight / 2);
      } else if (overlay.type === 'legend') {
        // Render legend using theme
        renderLegendContent(ctx, overlayWidth, overlayHeight);
      } else if (overlay.type === 'logo') {
        const config = overlay.config as { logoUrl: string };
        // Logo rendering would require loading the image
        ctx.font = '12px Arial';
        ctx.fillStyle = '#666';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Logo', overlayWidth / 2, overlayHeight / 2);
      }
      
      ctx.restore();
    });
    ctx.restore();
    
    // Draw coordinate tooltip when dragging (AFTER rotation restore - stays fixed to screen)
    if (isDraggingMarker && draggedMarkerCoords) {
      const tooltipX = 10;
      const tooltipY = height - 40;
      
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.fillRect(tooltipX, tooltipY, 200, 30);
      
      ctx.font = '12px monospace';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'left';
      ctx.fillText(
        `Lat: ${draggedMarkerCoords.lat.toFixed(6)}`,
        tooltipX + 5,
        tooltipY + 12
      );
      ctx.fillText(
        `Long: ${draggedMarkerCoords.long.toFixed(6)}`,
        tooltipX + 5,
        tooltipY + 24
      );
    }
  }, [baseMapImage, locations, viewState, calculateCanvasDimensions, hoveredMarkerId, draggedMarkerId, isDraggingMarker, draggedMarkerCoords, collisionResult, overlays, hoveredOverlayId, draggedOverlayId, isDraggingOverlay]);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? -10 : 10;
    const newZoomLevel = Math.max(10, Math.min(2000, viewState.zoomLevel + delta));
    
    onViewChange({
      ...viewState,
      zoomLevel: newZoomLevel,
    });
  }, [viewState, onViewChange]);

  // Handle mouse down for panning or marker dragging
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    
    // Check if clicking on an overlay first (overlays are on top)
    const overlay = findOverlayAtPosition(canvasX, canvasY);
    
    if (overlay && onOverlayDrag) {
      // Start dragging overlay
      setIsDraggingOverlay(true);
      setDraggedOverlayId(overlay.id);
      setOverlayDragOffset({
        x: canvasX - overlay.position.x,
        y: canvasY - overlay.position.y
      });
      return;
    }
    
    // Check if clicking on a marker
    const marker = findMarkerAtPosition(canvasX, canvasY);
    
    if (marker && onLocationDrag) {
      // Start dragging marker
      setIsDraggingMarker(true);
      setDraggedMarkerId(marker.id);
      setDraggedMarkerCoords({ lat: marker.lat, long: marker.long });
    } else {
      // Start panning
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  }, [findMarkerAtPosition, findOverlayAtPosition, onLocationDrag, onOverlayDrag]);

  // Handle mouse move for panning or marker dragging
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    
    if (isDraggingOverlay && draggedOverlayId && onOverlayDrag) {
      // Update overlay position
      const newX = canvasX - overlayDragOffset.x;
      const newY = canvasY - overlayDragOffset.y;
      
      // Constrain to canvas bounds
      const { width, height } = calculateCanvasDimensions();
      const draggedOverlay = overlays.find(o => o.id === draggedOverlayId);
      
      if (draggedOverlay) {
        const constrainedX = Math.max(0, Math.min(newX, width - draggedOverlay.size.width));
        const constrainedY = Math.max(0, Math.min(newY, height - draggedOverlay.size.height));
        
        onOverlayDrag(draggedOverlayId, { x: constrainedX, y: constrainedY });
      }
    } else if (isDraggingMarker && draggedMarkerId && onLocationDrag && baseMapImage) {
      // Update marker position
      const markerPixel = canvasToMarkerPixel(canvasX, canvasY);
      
      // Convert pixel to geographic coordinates for display
      // This is a simplified conversion - in production, use proper GeoTIFF transformation
      const draggedMarker = locations.find(loc => loc.id === draggedMarkerId);
      if (draggedMarker) {
        // Calculate approximate lat/long based on pixel offset from original position
        const pixelDeltaX = markerPixel.x - draggedMarker.pixelX;
        const pixelDeltaY = markerPixel.y - draggedMarker.pixelY;
        
        // Rough approximation: 1 pixel = 0.0001 degrees (will be replaced with proper transform)
        const latDelta = -pixelDeltaY * 0.0001;
        const longDelta = pixelDeltaX * 0.0001;
        
        const newLat = draggedMarker.lat + latDelta;
        const newLong = draggedMarker.long + longDelta;
        
        setDraggedMarkerCoords({ lat: newLat, long: newLong });
        onLocationDrag(draggedMarkerId, markerPixel.x, markerPixel.y);
      }
    } else if (isPanning) {
      // Pan the canvas
      const deltaX = e.clientX - panStart.x;
      const deltaY = e.clientY - panStart.y;
      
      onViewChange({
        ...viewState,
        pixelOffsetX: viewState.pixelOffsetX + deltaX,
        pixelOffsetY: viewState.pixelOffsetY + deltaY,
      });
      
      setPanStart({ x: e.clientX, y: e.clientY });
    } else {
      // Update hover state
      const overlay = findOverlayAtPosition(canvasX, canvasY);
      const marker = findMarkerAtPosition(canvasX, canvasY);
      
      setHoveredOverlayId(overlay ? overlay.id : null);
      setHoveredMarkerId(marker ? marker.id : null);
    }
  }, [isDraggingOverlay, draggedOverlayId, overlayDragOffset, isDraggingMarker, draggedMarkerId, isPanning, panStart, viewState, onViewChange, onLocationDrag, onOverlayDrag, canvasToMarkerPixel, findMarkerAtPosition, findOverlayAtPosition, locations, overlays, baseMapImage, calculateCanvasDimensions]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    if (isDraggingOverlay && draggedOverlayId && onOverlayDragEnd) {
      // Finalize overlay drag
      const draggedOverlay = overlays.find(o => o.id === draggedOverlayId);
      if (draggedOverlay) {
        onOverlayDragEnd(draggedOverlayId, draggedOverlay.position);
      }
    }
    
    if (isDraggingMarker && draggedMarkerId && onLocationDragEnd) {
      // Finalize marker drag
      const draggedMarker = locations.find(loc => loc.id === draggedMarkerId);
      if (draggedMarker) {
        onLocationDragEnd(draggedMarkerId, draggedMarker.pixelX, draggedMarker.pixelY);
      }
    }
    
    setIsPanning(false);
    setIsDraggingMarker(false);
    setDraggedMarkerId(null);
    setDraggedMarkerCoords(null);
    setIsDraggingOverlay(false);
    setDraggedOverlayId(null);
  }, [isDraggingOverlay, draggedOverlayId, isDraggingMarker, draggedMarkerId, onLocationDragEnd, onOverlayDragEnd, locations, overlays]);

  // Handle touch events for mobile
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length !== 1) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const canvasX = e.touches[0].clientX - rect.left;
    const canvasY = e.touches[0].clientY - rect.top;
    
    // Check if touching a marker
    const marker = findMarkerAtPosition(canvasX, canvasY);
    
    if (marker && onLocationDrag) {
      setIsDraggingMarker(true);
      setDraggedMarkerId(marker.id);
      setDraggedMarkerCoords({ lat: marker.lat, long: marker.long });
    } else {
      setIsPanning(true);
      setPanStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  }, [findMarkerAtPosition, onLocationDrag]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length !== 1) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const canvasX = e.touches[0].clientX - rect.left;
    const canvasY = e.touches[0].clientY - rect.top;
    
    if (isDraggingMarker && draggedMarkerId && onLocationDrag && baseMapImage) {
      // Update marker position
      const markerPixel = canvasToMarkerPixel(canvasX, canvasY);
      
      const draggedMarker = locations.find(loc => loc.id === draggedMarkerId);
      if (draggedMarker) {
        const pixelDeltaX = markerPixel.x - draggedMarker.pixelX;
        const pixelDeltaY = markerPixel.y - draggedMarker.pixelY;
        
        const latDelta = -pixelDeltaY * 0.0001;
        const longDelta = pixelDeltaX * 0.0001;
        
        const newLat = draggedMarker.lat + latDelta;
        const newLong = draggedMarker.long + longDelta;
        
        setDraggedMarkerCoords({ lat: newLat, long: newLong });
        onLocationDrag(draggedMarkerId, markerPixel.x, markerPixel.y);
      }
    } else if (isPanning) {
      const deltaX = e.touches[0].clientX - panStart.x;
      const deltaY = e.touches[0].clientY - panStart.y;
      
      onViewChange({
        ...viewState,
        pixelOffsetX: viewState.pixelOffsetX + deltaX,
        pixelOffsetY: viewState.pixelOffsetY + deltaY,
      });
      
      setPanStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  }, [isDraggingMarker, draggedMarkerId, isPanning, panStart, viewState, onViewChange, onLocationDrag, canvasToMarkerPixel, locations, baseMapImage]);

  const handleTouchEnd = useCallback(() => {
    if (isDraggingMarker && draggedMarkerId && onLocationDragEnd) {
      const draggedMarker = locations.find(loc => loc.id === draggedMarkerId);
      if (draggedMarker) {
        onLocationDragEnd(draggedMarkerId, draggedMarker.pixelX, draggedMarker.pixelY);
      }
    }
    
    setIsPanning(false);
    setIsDraggingMarker(false);
    setDraggedMarkerId(null);
    setDraggedMarkerCoords(null);
  }, [isDraggingMarker, draggedMarkerId, onLocationDragEnd, locations]);

  // Set up wheel event listener
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Render canvas when dependencies change
  useEffect(() => {
    renderCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseMapImage, locations, viewState, hoveredMarkerId, draggedMarkerId, isDraggingMarker, draggedMarkerCoords, collisionResult, overlays, hoveredOverlayId, draggedOverlayId, isDraggingOverlay, activeTheme, getMarkerColorFromTheme, loadedGeoTIFF]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      renderCanvas();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [renderCanvas]);

  return (
    <div className="design-canvas-container" ref={containerRef}>
      {locations.some(loc => loc.manuallyAdjusted) && onUndoManualAdjustment && (
        <div className="canvas-controls">
          <div className="undo-controls">
            <select 
              className="undo-select"
              onChange={(e) => {
                if (e.target.value) {
                  onUndoManualAdjustment(e.target.value);
                  e.target.value = '';
                }
              }}
              defaultValue=""
            >
              <option value="" disabled>Undo adjustment...</option>
              {locations
                .filter(loc => loc.manuallyAdjusted)
                .map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
      )}
      <div className="canvas-wrapper">
        <canvas
          ref={canvasRef}
          className="design-canvas"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ 
            cursor: isDraggingOverlay ? 'grabbing' : isDraggingMarker ? 'grabbing' : hoveredOverlayId ? 'move' : hoveredMarkerId ? 'pointer' : isPanning ? 'grabbing' : 'grab'
          }}
        />
      </div>
      {isDraggingMarker && (
        <div className="drag-hint">
          Drag to reposition marker. Release to confirm.
        </div>
      )}
      {isDraggingOverlay && (
        <div className="drag-hint">
          Drag to reposition overlay. Release to confirm.
        </div>
      )}
    </div>
  );
};
